import { OptimizationCostBreakdown } from '@paragon/data-connections';

export type OptimizationCostGroupKey = 'material' | 'labor';

interface OptimizationCostComparisonFigures {
  readonly shareOfDesignTotal?: number;
  readonly deltaFromPrevious?: number;
}

export interface OptimizationCostLineItem extends OptimizationCostComparisonFigures {
  readonly term: string;
  readonly label: string;
  readonly amount: number;
}

export interface OptimizationCostGroup extends OptimizationCostComparisonFigures {
  readonly key: OptimizationCostGroupKey;
  readonly label: string;
  readonly subtotal: number;
  readonly lineItems: readonly OptimizationCostLineItem[];
}

export interface GroupedOptimizationCostBreakdown {
  readonly total: number;
  readonly groups: readonly OptimizationCostGroup[];
}

type CostTerm = Exclude<keyof OptimizationCostBreakdown, 'total'>;

const COST_TERMS: Record<CostTerm, { readonly label: string; readonly group: OptimizationCostGroupKey }> = {
  lumber: { label: 'Lumber', group: 'material' },
  plate: { label: 'Plates', group: 'material' },
  bracing: { label: 'Bracing', group: 'material' },
  boardFeet: { label: 'Board feet', group: 'labor' },
  member: { label: 'Members', group: 'labor' },
  uniqueMember: { label: 'Unique members', group: 'labor' },
  cut: { label: 'Cuts', group: 'labor' },
  joint: { label: 'Joints', group: 'labor' },
  splice: { label: 'Splices', group: 'labor' },
  plateLabor: { label: 'Plate labor', group: 'labor' },
  plyLabor: { label: 'Ply assembly', group: 'labor' },
};

// Insertion order is display order.
const GROUP_LABELS: Record<OptimizationCostGroupKey, string> = {
  material: 'Material',
  labor: 'Labor',
};

export function mapBreakdownByTermToViewModel(breakdown: OptimizationCostBreakdown): GroupedOptimizationCostBreakdown {
  const allTerms = Object.keys(COST_TERMS) as CostTerm[];
  const groups = (Object.keys(GROUP_LABELS) as OptimizationCostGroupKey[]).map((key) => {
    const lineItems = allTerms
      .filter((term) => COST_TERMS[term].group === key)
      .map((term) => ({ term, label: COST_TERMS[term].label, amount: breakdown[term] }));
    const subtotal = lineItems.reduce((runningSubtotal, lineItem) => runningSubtotal + lineItem.amount, 0);
    return { key, label: GROUP_LABELS[key], subtotal, lineItems };
  });

  return { total: breakdown.total, groups };
}

/**
 * What the optimized design saves over the previous one. Derived from the same whole-cent values the figures are
 * displayed as, so a saving never disagrees with the two totals it sits between.
 */
export function calculateOptimizationSavings(previousTotal: number, optimizedTotal: number): number {
  return (Math.round(previousTotal * 100) - Math.round(optimizedTotal * 100)) / 100;
}

export function annotateOptimizedBreakdownForComparison(
  optimized: GroupedOptimizationCostBreakdown,
  previous: GroupedOptimizationCostBreakdown,
): GroupedOptimizationCostBreakdown {
  const shareOfDesignTotal = (amount: number) => (optimized.total === 0 ? 0 : amount / optimized.total);
  const previousGroupsByKey = new Map(previous.groups.map((group) => [group.key, group]));

  const groups = optimized.groups.map((group) => {
    const previousGroup = previousGroupsByKey.get(group.key);
    const previousAmountByTerm = new Map((previousGroup?.lineItems ?? []).map((item) => [item.term, item.amount]));

    const lineItems = group.lineItems.map((lineItem) => ({
      ...lineItem,
      shareOfDesignTotal: shareOfDesignTotal(lineItem.amount),
      deltaFromPrevious: lineItem.amount - (previousAmountByTerm.get(lineItem.term) ?? 0),
    }));

    return {
      ...group,
      lineItems,
      shareOfDesignTotal: shareOfDesignTotal(group.subtotal),
      deltaFromPrevious: group.subtotal - (previousGroup?.subtotal ?? 0),
    };
  });

  return { ...optimized, groups };
}
